import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { notificacoesApi, type Notificacao } from './notificacoesApi'
import { NOMES_DOS_TIPOS, tempoRelativo } from './notificacoesRegras'

/**
 * A lista das notificações do workspace: "Só por ler", "Marcar todas
 * como lidas" e cada uma a abrir no sítio (marca-a como lida). Serve o
 * sino (no desktop) e a página das notificações (no telemóvel não há
 * lugar para o sino na barra de topo).
 */
export default function ListaNotificacoes({
  limite = 30,
  aoMudarContagem,
  aoAbrir,
  className = '',
}: {
  limite?: number
  aoMudarContagem?: (n: number) => void
  /** Chamado antes de navegar (o sino fecha-se). */
  aoAbrir?: () => void
  className?: string
}) {
  const navigate = useNavigate()
  const [itens, setItens] = useState<Notificacao[] | null>(null)
  const [porLer, setPorLer] = useState(0)
  const [erro, setErro] = useState<string | null>(null)
  const [soPorLer, setSoPorLer] = useState(false)

  useEffect(() => {
    let vivo = true
    notificacoesApi
      .listar(soPorLer, limite)
      .then((r) => {
        if (!vivo) return
        setItens(r.itens)
        setPorLer(r.por_ler)
        aoMudarContagem?.(r.por_ler)
        setErro(null)
      })
      .catch((e: unknown) => {
        if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível ler as notificações.')
      })
    return () => {
      vivo = false
    }
  }, [soPorLer, limite, aoMudarContagem])

  const contar = (n: number) => {
    setPorLer(n)
    aoMudarContagem?.(n)
  }

  const abrir = (n: Notificacao) => {
    aoAbrir?.()
    if (!n.lida_em) {
      const agora = new Date().toISOString()
      setItens((xs) => xs?.map((x) => (x.id === n.id ? { ...x, lida_em: agora } : x)) ?? null)
      notificacoesApi
        .marcarLidas([n.id])
        .then((r) => contar(r.por_ler))
        .catch(() => {})
    }
    // A ligação é um caminho da plataforma; nunca sai do domínio.
    if (n.ligacao.startsWith('/')) navigate(n.ligacao)
  }

  const todasLidas = () => {
    notificacoesApi
      .marcarTodasLidas()
      .then((r) => {
        contar(r.por_ler)
        const agora = new Date().toISOString()
        setItens((xs) => xs?.map((x) => (x.lida_em ? x : { ...x, lida_em: agora })) ?? null)
      })
      .catch(() => {})
  }

  return (
    <div className={`flex min-h-0 flex-col text-xs ${className}`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <span className="font-medium text-[var(--color-text)]">Notificações</span>
        <label className="ml-auto flex items-center gap-1 text-[var(--color-text-muted)]">
          <input type="checkbox" checked={soPorLer} onChange={(e) => setSoPorLer(e.target.checked)} />
          Só por ler
        </label>
        <button
          type="button"
          onClick={todasLidas}
          disabled={porLer === 0}
          className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
        >
          Marcar todas como lidas
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {erro && <p className="px-3 py-3 text-[var(--color-error-text)]">{erro}</p>}
        {!erro && itens === null && <p className="px-3 py-3 text-[var(--color-text-muted)]">A carregar…</p>}
        {!erro && itens?.length === 0 && (
          <p className="px-3 py-3 text-[var(--color-text-muted)]">
            {soPorLer ? 'Sem notificações por ler.' : 'Ainda não há notificações.'}
          </p>
        )}
        {itens && itens.length > 0 && (
          <ul>
            {itens.map((n) => (
              <li key={n.id} className="border-b border-[var(--color-border)] last:border-b-0">
                <button
                  type="button"
                  onClick={() => abrir(n)}
                  className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-[var(--color-bg)] ${
                    n.lida_em ? 'opacity-70' : ''
                  }`}
                >
                  <span className="flex items-center gap-2 text-[10px] text-[var(--color-text-dim)]">
                    {!n.lida_em && (
                      <span
                        className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-accent)]"
                        aria-label="Por ler"
                      />
                    )}
                    <span>{NOMES_DOS_TIPOS[n.tipo] ?? n.tipo}</span>
                    <span className="ml-auto">{tempoRelativo(n.criada_em)}</span>
                  </span>
                  <span className="font-medium text-[var(--color-text)]">{n.titulo}</span>
                  {n.resumo && <span className="text-[var(--color-text-muted)]">{n.resumo}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
